import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useRef,
} from "react";
import { BloombergAnalyticsProvider, initialize } from "@bbnpm/bb-analytics";
import { getUser, getUserUuid } from "../client/api/bsso";
import type { User } from "../client/api/bsso";
import {
  CUSTOM_DIMENSIONS,
  MATOMO_TIERS_CONFIG,
} from "../client/config/matomo";
import { useFullNotification } from "../client/utility/full-notification";

// Extend User type with potentially available fields for analytics
interface ExtendedUser extends User {
  uuid?: string;
  emailAddress?: string;
}

interface AnalyticsContextType {
  provider: BloombergAnalyticsProvider | null;
  trackEvent: (
    category: string,
    action: string,
    name?: string,
    customDimensions?: Record<string, any>,
  ) => void;
  setCustomDimension: (dimensions: Record<string, any>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  provider: null,
  trackEvent: () => {},
  setCustomDimension: () => {},
});

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [provider, setProvider] = useState<BloombergAnalyticsProvider | null>(
    null,
  );
  const [user, setUser] = useState<ExtendedUser | null>(null); // State to store user info
  const [loading, setLoading] = useState(true); // State to handle loading status
  const notif = useFullNotification();

  // Debugging logs
  useEffect(() => {
    if (loading) {
      console.log("Loading...");
    }
  }, [user, loading]);

  useEffect(() => {
    if (provider) {
      console.log("Analytics Provider initialized.");
    } else {
      console.log("Analytics Provider is not yet initialized.");
    }
  }, [provider]);

  // Fetch the user info asynchronously when the component mounts
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const [userResult, uuidResult] = await Promise.allSettled([
          getUser(notif),
          getUserUuid(notif),
        ]);

        const userData =
          userResult.status === "fulfilled" ? userResult.value : null;
        const uuidData =
          uuidResult.status === "fulfilled" ? uuidResult.value : null;

        if (userResult.status === "rejected") {
          console.log("Failed to fetch user data:", userResult.reason);
        }
        if (uuidResult.status === "rejected") {
          console.log("Failed to fetch UUID data:", uuidResult.reason);
        }

        const combinedUser: ExtendedUser = {
          firstName: userData?.firstName || "",
          lastName: userData?.lastName || "",
          authSource: userData?.authSource,
          sessionId: userData?.sessionId || null,
          emailAddress: (userData as any)?.emailAddress,
          uuid: uuidData?.uuid || (userData as any)?.uuid,
        };

        setUser(combinedUser);
      } catch (error: any) {
        console.error("Unexpected error in fetchUser:", error);
        notif.addError({ message: "Error fetching user information." });
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [notif]);

  // 1. Initialize Bloomberg Analytics Provider
  useEffect(() => {
    let tierConfig;
    if (window.location.host.includes("prod")) {
      tierConfig = MATOMO_TIERS_CONFIG.PROD;
    } else if (window.location.host.includes("alpha")) {
      tierConfig = MATOMO_TIERS_CONFIG.ALPHA;
    } else {
      tierConfig = MATOMO_TIERS_CONFIG.DEV;
    }

    console.log(
      `Matomo siteId: ${tierConfig.siteId}. Matomo environment: ${tierConfig.environment}.`,
    );

    const newProvider = new BloombergAnalyticsProvider({
      ...tierConfig,
      ...CUSTOM_DIMENSIONS,
    });

    setProvider(newProvider);
    initialize({ provider: newProvider });
    // {SDSK 1312799008 <GO>} for consentless tracking approval
    newProvider.setIsConsented();
  }, []); // Run once on component mount

  // 2. Handle setting/resetting user ID and custom dimensions when user is available
  useEffect(() => {
    if (!provider || loading || !user) return;

    // Set user ID if we have UUID from either source
    if (user?.uuid) {
      provider.setUserId(user.uuid);
      console.log("Set user ID:", user.uuid);
    } else {
      console.log("No UUID available, reset user ID");
      provider.resetUserId();
    }

    // Set custom dimensions with whatever data we have
    const customDimensions: Record<string, string> = {};

    // Full Name: only set if we have both first and last name
    if (user.firstName && user.lastName) {
      const fullName = `${user.firstName} ${user.lastName}`;
      const emailPrefix = user.emailAddress?.split("@")?.[0] || "";
      customDimensions["Full Name"] = emailPrefix
        ? `${fullName} (${emailPrefix})`
        : fullName;
    }

    // Auth Source: set if available, otherwise use fallback
    customDimensions["Auth Source"] = user.authSource || "unknown";

    // Only set dimensions if we have some data to set
    if (Object.keys(customDimensions).length > 0) {
      provider.setDimension(customDimensions);
      console.log("Set custom dimensions:", customDimensions);
    } else {
      console.log("No user data available for custom dimensions");
    }
  }, [provider, user, loading]);

  // 3. Add pageview tracking
  const location = window.location;
  const isInitialPageviewSent = useRef(false);

  useEffect(() => {
    if (!provider || isInitialPageviewSent.current || loading) return;

    const handlePageView = () => {
      const payload = {
        url: location.pathname + location.search + location.hash,
        title: document.title,
      };
      console.log("Pageview:", payload);
      provider.pageView(payload);
    };

    // Send initial pageview
    if (!isInitialPageviewSent.current) {
      handlePageView();
      isInitialPageviewSent.current = true;
    }

    // Listen for history changes to track subsequent pageviews
    const onPopState = () => {
      handlePageView();
    };

    // Override pushState and replaceState to fire handlePageView
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    // Override pushState to fire handlePageView
    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      handlePageView();
    };

    // Override replaceState to fire handlePageView
    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      handlePageView();
    };

    // Listen for popstate event to track back/forward navigation
    window.addEventListener("popstate", onPopState);

    // Cleanup event listeners and overrides when component unmounts
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [provider, loading, user]);

  // If still loading, show a loading indicator
  if (loading) {
    return <div>Loading...</div>;
  }

  const trackEvent = (
    category: string,
    action: string,
    name?: string,
    customDimensions?: Record<string, any>,
  ) => {
    if (!provider) return;

    const eventObject: any = {
      category,
      action,
    };

    if (name) {
      eventObject.label = name;
    }

    // If we have event-specific custom dimensions, add them
    if (customDimensions && Object.keys(customDimensions).length > 0) {
      // For event-specific dimensions, use the key directly (not mapped to IDs)
      eventObject.customDimension = customDimensions;
    }

    console.log("Tracking event:", eventObject);
    provider.event(eventObject);
  };

  // Function to set custom dimensions dynamically
  const setCustomDimension = (dimensions: Record<string, any>) => {
    if (!provider) return;

    provider.setDimension(dimensions);
    console.log("Set additional custom dimensions:", dimensions);
  };

  return (
    <AnalyticsContext.Provider
      value={{ provider, trackEvent, setCustomDimension }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextType => {
  return useContext(AnalyticsContext);
};
